import React, { useRef, useEffect, useContext, useState } from "react";
import { CommonContext } from "../hooks/CommonContext";
import { getInitials } from "utils/commonUtils";
import ParticipantCameraOffBox from "./ui-components/ParticipantCameraOffBox";
import clsx from "clsx";
import PropTypes from "prop-types";
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaMagic,
  FaSun,
  FaMoon,
  FaAdjust,
  FaTimes,
} from "react-icons/fa";

const CameraFeed = ({ className = "", isLgIcon = false }) => {
  const localVideoCamera = useRef(null);
  const { localStream, userOwnData, isInMeetingRoom } =
    useContext(CommonContext);

  // Video effect states
  const [showControls, setShowControls] = useState(false);
  const [videoEffects, setVideoEffects] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
  });

  useEffect(() => {
    if (localStream.current && localVideoCamera.current) {
      localVideoCamera.current.srcObject = localStream.current;
      localVideoCamera.current.muted = true;
    }
    // eslint-disable-next-line
  }, [localStream.current, localStream]);

  // Re-attach srcObject when effects pipeline swaps the video track
  useEffect(() => {
    const onTrackChanged = (e) => {
      if (localVideoCamera.current && e.detail?.stream) {
        localVideoCamera.current.srcObject = e.detail.stream;
        localVideoCamera.current.muted = true;
        localVideoCamera.current.play().catch(() => {});
      }
    };
    const onFilterChanged = (e) => {
      if (localVideoCamera.current) {
        localVideoCamera.current.style.filter = e.detail?.css || "none";
      }
    };
    window.addEventListener("bizro:videoTrackChanged", onTrackChanged);
    window.addEventListener("bizro:videoFilter", onFilterChanged);
    return () => {
      window.removeEventListener("bizro:videoTrackChanged", onTrackChanged);
      window.removeEventListener("bizro:videoFilter", onFilterChanged);
    };
  }, []);

  // Close panel when camera turns off
  useEffect(() => {
    if (!userOwnData.isCameraOn) {
      setShowControls(false);
    }
  }, [userOwnData.isCameraOn]);

  const isModified =
    videoEffects.brightness !== 100 ||
    videoEffects.contrast !== 100 ||
    videoEffects.saturation !== 100;

  const resetEffects = () => {
    setVideoEffects({ brightness: 100, contrast: 100, saturation: 100 });
  };

  const applyPreset = (preset) => {
    const presets = {
      brighten: { brightness: 130, contrast: 110, saturation: 105 },
      warm: { brightness: 110, contrast: 105, saturation: 115 },
      cool: { brightness: 105, contrast: 105, saturation: 90 },
    };
    if (presets[preset]) setVideoEffects(presets[preset]);
    else resetEffects();
  };

  return (
    <div className={clsx("w-100 h-100")} style={{ position: "relative" }}>
      {/* ── Video element ── */}
      <video
        id="localVideo"
        ref={localVideoCamera}
        className={`video-camera ${className} ${
          userOwnData.isCameraOn ? " " : "d-none"
        }`}
        autoPlay
        playsInline
        style={{
          borderRadius: "12px",
          transition: "filter 0.3s ease",
          objectFit: "cover",
        }}
        width="100%"
        height="100%"
      />

      {!userOwnData.isCameraOn && (
        <ParticipantCameraOffBox
          colorCode={userOwnData.colorCode}
          latter={getInitials(userOwnData)}
          isAudioOn={userOwnData.isAudioOn}
          isLgIcon={isLgIcon}
        />
      )}

      {userOwnData.isCameraOn && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowControls((v) => !v);
          }}
          title="Video effects"
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            width: "32px",
            height: "32px",
            borderRadius: "8px",
            border: "none",
            background: showControls
              ? "rgba(99, 102, 241, 0.9)"
              : "rgba(0,0,0,0.45)",
            backdropFilter: "blur(6px)",
            color: showControls ? "#fff" : "rgba(255,255,255,0.75)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            zIndex: 11,
            flexShrink: 0,
          }}
        >
          <FaMagic size={13} />
          {/* Active-preset dot */}
          {isModified && !showControls && (
            <span
              style={{
                position: "absolute",
                top: "4px",
                right: "4px",
                width: "7px",
                height: "7px",
                borderRadius: "50%",
                background: "#818cf8",
                border: "1.5px solid rgba(0,0,0,0.4)",
              }}
            />
          )}
        </button>
      )}

      {userOwnData.isCameraOn && showControls && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            bottom: "54px",
            left: "10px",
            background: "rgba(15, 15, 20, 0.92)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "14px",
            padding: "14px 16px 12px",
            boxShadow:
              "0 8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.07)",
            zIndex: 10,
            width: "260px",
            animation: "fadeSlideUp 0.18s ease",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                color: "#e2e8f0",
                fontSize: "12px",
                fontWeight: "600",
                letterSpacing: "0.02em",
              }}
            >
              <FaMagic size={11} style={{ color: "#818cf8" }} />
              Video Effects
            </div>
            <button
              onClick={() => setShowControls(false)}
              style={{
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.4)",
                cursor: "pointer",
                padding: "0",
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              <FaTimes size={11} />
            </button>
          </div>

          {/* Quick Presets */}
          <div style={{ display: "flex", gap: "6px", marginBottom: "14px" }}>
            {[
              {
                key: "brighten",
                label: "Brighten",
                icon: <FaSun size={10} />,
                color: "#fbbf24",
              },
              {
                key: "warm",
                label: "Warm",
                icon: <FaSun size={10} />,
                color: "#f97316",
              },
              {
                key: "cool",
                label: "Cool",
                icon: <FaMoon size={10} />,
                color: "#60a5fa",
              },
            ].map(({ key, label, icon, color }) => {
              const active =
                JSON.stringify(videoEffects) ===
                JSON.stringify(
                  {
                    brighten: {
                      brightness: 130,
                      contrast: 110,
                      saturation: 105,
                    },
                    warm: { brightness: 110, contrast: 105, saturation: 115 },
                    cool: { brightness: 105, contrast: 105, saturation: 90 },
                  }[key],
                );

              return (
                <button
                  key={key}
                  onClick={() => applyPreset(key)}
                  style={{
                    flex: 1,
                    background: active
                      ? `rgba(${color},0.15)`
                      : "rgba(255,255,255,0.06)",
                    border: active
                      ? `1px solid ${color}55`
                      : "1px solid rgba(255,255,255,0.1)",
                    color: active ? color : "rgba(255,255,255,0.65)",
                    fontSize: "11px",
                    padding: "5px 4px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    transition: "all 0.15s ease",
                    fontWeight: "500",
                  }}
                >
                  {icon} {label}
                </button>
              );
            })}
          </div>

          {/* Sliders */}
          {[
            {
              key: "brightness",
              label: "Brightness",
              icon: <FaSun size={10} />,
            },
            {
              key: "contrast",
              label: "Contrast",
              icon: <FaAdjust size={10} />,
            },
            {
              key: "saturation",
              label: "Saturation",
              icon: <FaAdjust size={10} />,
            },
          ].map(({ key, label, icon }) => (
            <div key={key} style={{ marginBottom: "10px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "5px",
                }}
              >
                <label
                  style={{
                    fontSize: "11px",
                    color: "rgba(255,255,255,0.55)",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  {icon} {label}
                </label>
                <span
                  style={{
                    fontSize: "10px",
                    color:
                      videoEffects[key] !== 100
                        ? "#818cf8"
                        : "rgba(255,255,255,0.3)",
                    fontWeight: "600",
                    minWidth: "36px",
                    textAlign: "right",
                  }}
                >
                  {videoEffects[key]}%
                </span>
              </div>
              <input
                type="range"
                min="50"
                max="150"
                value={videoEffects[key]}
                onChange={(e) =>
                  setVideoEffects((prev) => ({
                    ...prev,
                    [key]: parseInt(e.target.value),
                  }))
                }
                className="form-range"
                style={{ height: "3px" }}
              />
            </div>
          ))}

          {/* Divider + Reset */}
          <div
            style={{
              borderTop: "1px solid rgba(255,255,255,0.07)",
              paddingTop: "10px",
              marginTop: "2px",
            }}
          >
            <button
              onClick={resetEffects}
              disabled={!isModified}
              style={{
                width: "100%",
                background: isModified
                  ? "rgba(99,102,241,0.12)"
                  : "rgba(255,255,255,0.04)",
                border: isModified
                  ? "1px solid rgba(99,102,241,0.3)"
                  : "1px solid rgba(255,255,255,0.08)",
                color: isModified ? "#a5b4fc" : "rgba(255,255,255,0.25)",
                fontSize: "11px",
                padding: "6px",
                borderRadius: "8px",
                cursor: isModified ? "pointer" : "default",
                transition: "all 0.15s ease",
                fontWeight: "500",
              }}
            >
              Reset to default
            </button>
          </div>
        </div>
      )}

      {/* Keyframe animation injected once */}
      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ── Mic indicator + name label (meeting room only) ── */}
      {isInMeetingRoom && (
        <>
          <div
            className={`${userOwnData.isCameraOn ? " " : "d-none"}`}
            style={{ position: "absolute", top: "10px", right: "10px" }}
          >
            {userOwnData.isAudioOn ? (
              <FaMicrophone style={{ fontSize: "18px", color: "#fff" }} />
            ) : (
              <FaMicrophoneSlash
                className="text-red"
                style={{ fontSize: "18px" }}
              />
            )}
          </div>
          <div
            className={`${
              isLgIcon ? "video_comment_icon" : "other-video_comment_icon"
            }`}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div
                className="participant-text rounded"
                style={{ color: "#fff" }}
              >
                {userOwnData.fname} {userOwnData.lname}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

CameraFeed.propTypes = {
  className: PropTypes.string,
  isLgIcon: PropTypes.bool,
};

export default CameraFeed;
